// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import {
  ApplyBundleDialog,
  BundleApplyLine,
} from '@/bundle/components/apply-bundle-dialog';
import { buildBundleAppliedToast } from '@/bundle/utilities/bundle-applied-toast';
import { computePhysicalBundleApply } from '@/bundle/utilities/bundle-physical-apply';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormNumberTextField } from '@/components/form-number-text-field';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LibraryAddOutlined,
  UploadFileOutlined as UploadIcon,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getItems } from '@/items/api/get-items';
import { ItemSearch } from '@/items/components/item-search';
import { PurchaseOrder, PurchaseOrderItem } from '@/purchase-order/api/types';
import { ReturnOrder, ReturnOrderItem } from '@/return-order/api/types';
import { Shipment } from '@/shipment/api/types';
import { calculateShipmentQuantities } from '@/shipment/utilities/calculate-shipment-quantities';
import {
  lookupShipmentItemsBySkuFromItems,
  parseShipmentItemsCSV,
} from '@/shipment/utilities/parse-shipment-items-csv';
import { SparseItem, sparseItemSchema } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import {
  Controller,
  FieldValues,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { z } from 'zod';

// The maximum number of line items a shipment may carry
const SHIPMENT_MAX_ITEMS = 100;

const _shipmentFormItemSchema = z.object({
  item: sparseItemSchema.nullable(),
  quantity: z.number().nullable(),
});

type ShipmentFormItem = z.infer<typeof _shipmentFormItemSchema>;

interface InboundShipmentItemsTableProps {
  /**
   * The name of the field array in the form.
   * Defaults to 'items'.
   */
  name?: string;

  /**
   * Whether the shipment has been shipped (disables editing)
   */
  isShipped: boolean;

  /**
   * Whether the shipment has been received (disables editing)
   */
  isReceived: boolean;

  /**
   * Selected purchase order for PO mode columns
   */
  purchaseOrder: PurchaseOrder | null;

  /**
   * Selected return order for RO mode columns
   */
  returnOrder: ReturnOrder | null;

  /**
   * Shipments for the selected purchase/return order (used to calculate fulfilled quantities)
   */
  shipments: Shipment[];

  /**
   * From location ID for inventory lookup (RO mode only)
   */
  fromLocationId?: string;

  /**
   * Inventory map for displaying available quantities (RO mode only)
   * Structure: Map<locationId, Map<itemId, quantity>>
   */
  inventoryMap?: Map<string, Map<string, number>>;

  /**
   * Whether inventory is loading
   */
  isLoadingInventory?: boolean;

  /**
   * Whether inventory is fetching (including background refetch)
   */
  isFetchingInventory?: boolean;
}

/**
 * Table component for managing shipment items.
 *
 * IMPORTANT: This component must be wrapped in a FormProvider from react-hook-form.
 * The form must have an 'items' field (or the field specified by the 'name' prop)
 * that is an array of ShipmentFormItem objects.
 */
export function InboundShipmentItemsTable(
  props: InboundShipmentItemsTableProps
) {
  const {
    name = 'items',
    isShipped,
    isReceived,
    purchaseOrder,
    returnOrder,
    shipments,
    fromLocationId,
    inventoryMap,
    isLoadingInventory,
    isFetchingInventory,
  } = props;

  // Get form control and field array from context
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<FieldValues>();
  const { fields, append, remove, replace } = useFieldArray({ control, name });
  const currentItems = (watch(name) ?? []) as ShipmentFormItem[];

  const { showToast } = useToast();
  const applyBundleDialog = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch items for SKU lookup during CSV upload
  const { data: itemsData } = useQuery({
    queryKey: ['item', { includeArchived: true }],
    queryFn: () => getItems({ archived: true }),
  });

  /**
   * Handles CSV file upload button click by triggering the hidden file input.
   */
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Handles CSV file selection and parses the file to populate shipment items.
   * Looks up parts and products by SKU and replaces current items.
   */
  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;

        try {
          const parsedItems = parseShipmentItemsCSV(text);

          if (parsedItems.length === 0) {
            showToast({
              element: <>CSV file is empty or has no valid rows</>,
              severity: 'warning',
            });
            return;
          }

          const { formItems, notFoundSkus } = lookupShipmentItemsBySkuFromItems(
            parsedItems,
            itemsData?.results ?? []
          );

          if (formItems.length === 0) {
            showToast({
              element: (
                <>No matching SKUs found. Missing: {notFoundSkus.join(', ')}</>
              ),
              severity: 'error',
            });
            return;
          }

          // Replace current items with parsed items
          replace(formItems);

          // Show success message with any warnings
          if (notFoundSkus.length > 0) {
            showToast({
              element: (
                <>
                  Added {formItems.length} items. SKUs not found:{' '}
                  {notFoundSkus.join(', ')}
                </>
              ),
              severity: 'warning',
            });
          } else {
            showToast({
              element: <>Added {formItems.length} items from CSV</>,
              severity: 'success',
            });
          }
        } catch (error) {
          showToast({
            element: (
              <>
                {error instanceof Error
                  ? error.message
                  : 'Failed to parse CSV file'}
              </>
            ),
            severity: 'error',
          });
        }
      };

      reader.readAsText(file);

      // Reset the file input so the same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [itemsData, replace, showToast]
  );

  const isPOMode = !!purchaseOrder;
  const isROMode = !!returnOrder;
  const isOrderMode = isPOMode || isROMode;

  // Build a map of catalog item ID -> PO line item for quick lookups
  const poItemMap = useMemo(() => {
    const map = new Map<string, PurchaseOrderItem>();
    if (!purchaseOrder) return map;

    purchaseOrder.items.forEach((lineItem) => {
      // PurchaseOrderItem uses nested structure - item.item.id is the catalog item ID
      map.set(lineItem.item.id, lineItem);
    });
    return map;
  }, [purchaseOrder]);

  // Build a map of catalog item ID -> RO line item for quick lookups
  const roItemMap = useMemo(() => {
    const map = new Map<string, ReturnOrderItem>();
    if (!returnOrder) return map;

    returnOrder.items.forEach((lineItem) => {
      // ReturnOrderItem uses nested structure - item.item.id is the catalog item ID
      map.set(lineItem.item.id, lineItem);
    });
    return map;
  }, [returnOrder]);

  /**
   * Gets the available inventory text for an item (RO mode only).
   * Shows quantity at the from location.
   */
  const getAvailableText = (itemId: string | undefined): string => {
    // Only show for RO mode (when we have inventory data)
    if (!inventoryMap) return '';
    if (!itemId) return '\u00A0'; // Non-breaking space when no item selected
    if (!fromLocationId) return "Select 'from' location to show available";
    if (isLoadingInventory || isFetchingInventory) return 'Loading...';
    const quantity = inventoryMap.get(fromLocationId)?.get(itemId) ?? 0;
    return `${formatNumber(quantity)} available`;
  };

  // Get selected items to prevent duplicates - item is already a SparseItem
  const selectedItems: SparseItem[] = currentItems
    .filter(
      (formItem): formItem is ShipmentFormItem & { item: SparseItem } =>
        formItem.item !== null
    )
    .map((formItem) => formItem.item);

  const handleAddItem = () => {
    append({
      item: null,
      quantity: null,
    });
  };

  // Shipments forbid duplicate SKU rows and hold only physical items, so
  // applying a bundle sums each physical member's computed quantity into an
  // existing row, appends members with no existing row (up to the cap), and
  // drops service members.
  const handleApplyBundle = (lines: BundleApplyLine[], bundle: Bundle) => {
    const existingRows = (getValues(name) as ShipmentFormItem[]).map((row) => ({
      itemId: row.item?.id,
      quantity: row.quantity,
    }));
    const { updates, appends, fills, combined, appliedCount, skippedCount } =
      computePhysicalBundleApply(existingRows, lines, SHIPMENT_MAX_ITEMS);
    for (const update of updates) {
      setValue(`${name}.${update.index}.quantity`, update.quantity, {
        shouldValidate: true,
      });
    }
    for (const fill of fills) {
      setValue(`${name}.${fill.index}.item`, fill.line.item, {
        shouldValidate: true,
      });
      setValue(`${name}.${fill.index}.quantity`, fill.line.quantity, {
        shouldValidate: true,
      });
    }
    for (const appendLine of appends) {
      append({
        item: appendLine.item,
        quantity: appendLine.quantity,
      });
    }
    showToast({
      element: buildBundleAppliedToast({
        lineCount: appliedCount,
        bundleName: bundle.name,
        combined,
        skippedCount,
      }),
      severity: 'success',
    });
  };

  const isDisabled = isShipped || isReceived;

  return (
    <Box>
      {/* Hidden file input for CSV upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Header with Add button and CSV upload button */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1">Shipment items</Typography>
        <Stack direction="row" spacing={1}>
          <ConditionalTooltip
            arrow
            tooltipText={
              isReceived
                ? 'Items cannot be added to received shipments'
                : isShipped
                  ? 'Items cannot be added after shipment has shipped'
                  : undefined
            }
          >
            <Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
                disabled={isDisabled}
              >
                Add item
              </Button>
            </Box>
          </ConditionalTooltip>
          <ConditionalTooltip
            arrow
            tooltipText={
              isReceived
                ? 'Items cannot be added to received shipments'
                : isShipped
                  ? 'Items cannot be added after shipment has shipped'
                  : undefined
            }
          >
            <Box>
              <Button
                variant="outlined"
                startIcon={<LibraryAddOutlined />}
                onClick={applyBundleDialog.open}
                size="small"
                disabled={isDisabled}
              >
                Add bundle
              </Button>
            </Box>
          </ConditionalTooltip>
          <ConditionalTooltip
            arrow
            tooltipText={
              isReceived
                ? 'Items cannot be added to received shipments'
                : isShipped
                  ? 'Items cannot be added after shipment has shipped'
                  : 'Upload many line items from a CSV file with SKU and quantity columns'
            }
          >
            <Box>
              <Button
                onClick={handleUploadClick}
                size="small"
                disabled={isDisabled}
                sx={{ minWidth: 0, px: 1, height: '100%' }}
              >
                <UploadIcon fontSize="small" />
              </Button>
            </Box>
          </ConditionalTooltip>
        </Stack>
      </Stack>

      {/* Table */}
      <Table
        key={isOrderMode ? (isPOMode ? 'po-mode' : 'ro-mode') : 'standard'}
        size="small"
        sx={{
          '& .MuiTableCell-root': { pt: 2.5, pb: 1.5 },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root':
            {
              borderBottom: 'none',
            },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            {isOrderMode && (
              <>
                <TableCell align="right" sx={{ width: 100 }}>
                  {isPOMode ? 'Ordered' : 'Requested'}
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  {isPOMode ? 'Recorded' : 'Returned'}
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Need
                </TableCell>
              </>
            )}
            <TableCell align="right" sx={{ width: 200 }}>
              <Box sx={{ mr: 1.5 }}>Quantity</Box>
            </TableCell>
            <TableCell align="center" sx={{ width: 60 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => {
            const currentValue = currentItems[index];
            const itemId = currentValue?.item?.id;
            const poItem = itemId ? poItemMap.get(itemId) : undefined;
            const roItem = itemId ? roItemMap.get(itemId) : undefined;

            // Calculate order details (Ordered/Requested, Recorded/Returned, Need) from shipments
            const orderItem = poItem || roItem;
            const orderedQuantity = orderItem?.requestedQuantity ?? null;
            const quantities = calculateShipmentQuantities(
              shipments,
              currentValue?.item?.id
            );
            const recordedQuantity = orderItem
              ? quantities.pending +
                quantities.shipped +
                quantities.delivered +
                quantities.received
              : null;
            const needQuantity =
              orderedQuantity !== null && recordedQuantity !== null
                ? Math.max(0, orderedQuantity - recordedQuantity)
                : null;

            return (
              <TableRow key={field.id}>
                {/* Part/Product column */}
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <ConditionalTooltip
                    arrow
                    tooltipText={
                      isReceived
                        ? 'Items cannot be changed for received shipments'
                        : isShipped
                          ? 'Items cannot be changed after shipment has shipped'
                          : undefined
                    }
                    sx={{ width: '100%' }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Controller
                        control={control}
                        name={`items.${index}`}
                        render={({ field: itemField, fieldState }) => {
                          // The item in the form is already a SparseItem
                          const sparseItemValue = currentValue?.item ?? null;

                          return (
                            <ItemSearch
                              label="Item"
                              value={sparseItemValue}
                              physicalOnly={true}
                              onChange={(value) => {
                                itemField.onChange({
                                  item: value,
                                  quantity: currentValue?.quantity ?? null,
                                });
                              }}
                              hiddenItems={selectedItems.filter(
                                (item) => item.id !== currentValue?.item?.id
                              )}
                              error={
                                !!(
                                  fieldState.error as {
                                    item?: { message?: string };
                                  }
                                )?.item
                              }
                              helperText={
                                (
                                  fieldState.error as {
                                    item?: { message?: string };
                                  }
                                )?.item?.message
                              }
                              disabled={isDisabled}
                            />
                          );
                        }}
                      />
                    </Box>
                  </ConditionalTooltip>
                </TableCell>

                {/* Order detail columns (PO or RO) */}
                {isOrderMode && (
                  <>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="body2">
                          {orderedQuantity !== null ? orderedQuantity : '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="body2">
                          {recordedQuantity !== null ? recordedQuantity : '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="body2">
                          {needQuantity !== null ? needQuantity : '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </>
                )}

                {/* Quantity column */}
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <FormNumberTextField
                    fullWidth
                    nullable
                    numeric
                    label="Quantity"
                    control={control}
                    name={`items.${index}.quantity`}
                    disabled={isShipped}
                  />
                  {inventoryMap && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                    >
                      {getAvailableText(currentValue?.item?.id)}
                    </Typography>
                  )}
                </TableCell>

                {/* Actions column */}
                <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                  <Box sx={{ pt: 0.5 }}>
                    <ConditionalTooltip
                      arrow
                      tooltipText={
                        isReceived
                          ? 'Items cannot be removed from received shipments'
                          : isShipped
                            ? 'Items cannot be removed after shipment has shipped'
                            : fields.length <= 1
                              ? 'At least one item is required'
                              : undefined
                      }
                    >
                      <Box>
                        <IconButton
                          onClick={() => fields.length > 1 && remove(index)}
                          size="small"
                          disabled={fields.length <= 1 || isDisabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ConditionalTooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Total quantity summary */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 1,
          mr: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Total quantity:{' '}
          <Typography component="span" variant="body2" fontWeight="medium">
            {formatNumber(
              currentItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0)
            )}
          </Typography>
        </Typography>
      </Box>

      {/* Error message */}
      {errors[name]?.message && typeof errors[name].message === 'string' && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {errors[name].message as string}
        </Typography>
      )}
      <ApplyBundleDialog
        isOpen={applyBundleDialog.isOpen}
        onClose={applyBundleDialog.close}
        onApply={handleApplyBundle}
      />
    </Box>
  );
}
